import React from 'react'
import {useTranslation} from 'react-i18next'
import { DataGrid, Column, Paging, IColumnProps, IDataGridOptions, RemoteOperations } from 'devextreme-react/data-grid'
import {useLocalize, usePaginate} from 'hooks';
import _ from 'lodash'

export interface DataColumn extends IColumnProps {
    id: string | number
}

interface Props extends IDataGridOptions {
    indexRowLabel?: string,
    keyExpr?: string
    columns?: DataColumn[]
    actionsElement?: React.ComponentType,
    hideIndexRow?:boolean,
    children?: any
}

const DxfGridComponent = React.forwardRef<DataGrid<unknown, unknown>, Props>(
    (
        {
            indexRowLabel = "admin:applications.versions.numberCol",
            keyExpr,
            columns = [],
            hideIndexRow=false,
            actionsElement: ActionsElement,
            children,
            ...props
        },
        ref
    ) => {
        const {t} = useTranslation('common')
        const [page, setPage] = usePaginate()
        const {currentLanguage} = useLocalize()
        const isRTL = currentLanguage?.dir === 'rtl'


        function getRowIndex(pageIndex: number, pageSize: number, rowIndex: number) {
            return (pageIndex * pageSize) + rowIndex + 1
        }

        const withFilterRow = _.isArray(props.dataSource)
            ? !!props.dataSource.length
            : !!props.dataSource

        const statusTypes = [
            {id: true, label: 'active'},
            {id: false, label: 'inactive'},
        ]

        //Get FilterOperations ByDataType
        const filterOperation = (dataType: any) => {
            switch (dataType) {
                case 'string':
                    return ['contains', 'notcontains']
                case 'number':
                    return ['=', '<>', '<', '>', '<=', '>=']
                case 'boolean':
                    return []
                case 'date':
                    return ["=",'<=', '>=']
                case 'datetime':
                    return ['<=', '>=']
                default:
                    break
            }
        }

     const operationDescriptions = {
            "between": t("operations.between"),
            "contains": t("operations.contains"),
            "endsWith": t("operations.endsWith"),
            "equal": t("operations.equal"),
            "greaterThan": t("operations.greaterThan"),
            "greaterThanOrEqual": t("operations.greaterThanOrEqual"),
            "lessThan": t("operations.lessThan"),
            "lessThanOrEqual": t("operations.lessThanOrEqual"),
            "notContains": t("operations.notContains"),
            "notEqual": t("operations.notEqual"),
            "startsWith": t("operations.startsWith"),
            
     }

        return (
            <DataGrid
                ref={ref}
                keyExpr={keyExpr}
                columnMinWidth={50}
                hoverStateEnabled
                allowColumnResizing
                headerFilter={{ visible: false }}
                pager={{
                    allowedPageSizes: [25, 50, 100],
                    showPageSizeSelector: true,
                    showInfo: true,
                    visible: true,
                    infoText:`${t('common:page')} {0} ${t('common:of')} {1} ({2} ${t('common:items')})`,
                }}
                editing={{ confirmDelete: true }}
                filterRow={{
                    visible: withFilterRow,
                    operationDescriptions,
                    showAllText: t('operations.all'),
                    resetOperationText: t('operations.reset')
                }}
                rtlEnabled={isRTL}
                {...props}
            >
                <Column
                    visible={!hideIndexRow}
                    cellRender={(data: any) => {
                        return getRowIndex(
                            data.component.pageIndex(),
                            data.component.pageSize(),
                            data.rowIndex
                        )
                    }}
                    caption={t(indexRowLabel)}
                    width="100"
                    alignment="center"
                    dataType="number"
                />
                {columns.map(({ id, caption, dataType, ...col }) => (
                    <Column
                        key={id}
                        dataField={id}
                        cssClass="vertical-top"
                        caption={caption ? t(caption) : undefined}
                        alignment={currentLanguage?.dir === 'rtl' ? 'right' : 'left'}
                        filterOperations={filterOperation(dataType || 'string')}
                        dataType={dataType}
                        lookup={
                            dataType === 'boolean'
                                ? {
                                      allowClearing: true,
                                      dataSource: statusTypes.map(s => ({
                                          id: s.id,
                                          label: t(`common:sort.${s.label}`),
                                      })),
                                      valueExpr: 'id',
                                      displayExpr: 'label',
                                  }
                                : undefined
                        }
                        {...col}
                    />
                ))}
                {ActionsElement && (
                    <Column
                        fixedPosition="right"
                        cssClass="action-overflow"
                        alignment="center"
                        allowExporting={false}
                        width="70"
                        cellRender={cellProps => <ActionsElement {...cellProps} />}
                    />
                )}
                <Paging defaultPageSize={25} pageIndex={page} onPageIndexChange={setPage} />
                <RemoteOperations filtering={true} paging={true} />
                {children}
            </DataGrid>
        )
    }
)

export * from 'devextreme-react/data-grid'
export default DxfGridComponent
